import type { ReactNode } from 'react';
import { CodeChip } from '../ui/CodeChip';
import { EntityAvatar, entityHue } from '../ui/EntityAvatar';

/**
 * A PERSON, DRAWN AS A PICKER OPTION — the sibling of `warehouseLabel` for staff.
 *
 * Every staff dropdown in the app had grown the same string: `${fullName} · ${mobile}`. That is the
 * exact shape the dropdown rule exists to forbid — the dot is weaker than the spaces around it, so
 * "Prashant Jadhav · +919881472305" reads as one run-on line, and a list of twenty of them is a wall
 * of identical grey text with nothing for the eye to run down. Worse, a phone number is an
 * IDENTIFIER: it is the half you scan when two people share a name, and it was the half that
 * dissolved into the sentence.
 *
 * So an option becomes four aligned columns: the person's identity mark, their name, where they are
 * based, and the number — each in its own shape, each starting on one x down the list.
 */

export type StaffLike = {
  id?: string | number;
  fullName?: string | null;
  mobile?: string | null;
  profilePhotoUrl?: string | null;
  status?: string | null;
  territoryId?: string | number | null;
  role?: { code?: string | null } | null;
  roleCode?: string | null;
};

/** The role code whichever way the endpoint spelled it (`/users` nests it, some DTOs flatten it). */
export const staffRoleCode = (u: StaffLike): string => String(u.role?.code ?? u.roleCode ?? '');

/** Plain text a searchable Select matches on — name AND number, so either finds the person. */
export const staffText = (u: StaffLike): string =>
  [u.fullName, u.mobile].filter(Boolean).join(' ') || '—';

/**
 * A territory CODE as a chip in that territory's own identity hue — the same colour it wears beside
 * an avatar anywhere else, so "the Nashik one" is a colour before it is a word.
 *
 * `title` says **base** deliberately. `users.territory_id` is only a person's home territory; real
 * coverage lives in `territory_assignments` and an SP can hold many. A bare code here would be read
 * as "this is who covers it", and its ABSENCE as "this one doesn't" — neither of which this column
 * is entitled to claim.
 */
export function TerritoryCode({ code, name, className = '' }: { code: string; name?: string | null; className?: string }) {
  return (
    <CodeChip
      hue={entityHue(code)}
      title={`Based in ${[code, name].filter(Boolean).join(' — ')}`}
      className={`shrink-0 min-w-17 text-center ${className}`}
    >
      {code}
    </CodeChip>
  );
}

/** The phone number: neutral, tabular, and last. It is the tie-breaker between two people with the
 *  same name, not the thing being chosen — so it is a column you can check, never the loudest mark. */
export function StaffPhone({ mobile, className = '' }: { mobile: string; className?: string }) {
  return <CodeChip className={`shrink-0 min-w-29 text-center ${className}`}>{mobile}</CodeChip>;
}

/** A short standing fact about this person FOR THIS DECISION — e.g. "dealer's rep". Only ever passed
 *  for the rows it is true of: a mark on every row marks nothing ([[constant-glyph-marks-are-noise]]). */
export function StaffNote({ children, tone = 'brand' }: { children: ReactNode; tone?: 'brand' | 'gold' }) {
  const cls = tone === 'gold'
    ? 'border-harvest-500/45 bg-harvest-400/20 text-harvest-700 dark:border-harvest-400/40 dark:text-harvest-300'
    : 'border-brand-500/45 bg-brand-500/15 text-brand-700 dark:border-brand-400/40 dark:text-brand-300';
  return (
    <span className={`shrink-0 whitespace-nowrap rounded-pill border px-1.5 py-px type-overline ${cls}`}>
      {children}
    </span>
  );
}

export interface StaffLabelOpts {
  /** Territory master, keyed by id — supply it and the base-territory chip appears. */
  territories?: Map<string, { code?: string | null; name?: string | null }>;
  /**
   * Pills that state WHAT THIS PERSON IS, drawn immediately after the name — normally the
   * `RoleBadge`(s) for the roles they hold. A role belongs beside the name, never at the right edge
   * with the identifiers: in a list that mixes Heads and reps the role is what is being chosen
   * between, so it has to sit where the eye already is. Kept as a slot rather than derived here so
   * `lib/` stays presentation-only and one picker can badge something else (a team, a language).
   */
  badges?: ReactNode;
  /** Rendered at the right edge for this person only (see `StaffNote`). */
  note?: ReactNode;
  /** Photo diameter. 26 suits a dropdown row; the trigger uses the same so it doesn't jump on select. */
  size?: number;
}

/**
 * The full option row: identity mark · name · role pills · base territory · number · note.
 *
 * `flex` and `max-w-full` rather than `inline-flex`, for the reason `warehouseLabel` documents at
 * length — a Select's collapsed trigger wraps its label in a `truncate` span, which only ellipsises
 * INLINE content, so an inline-flex label sized to its own contents gets hard-clipped mid-chip. Here
 * the only thing allowed to give is the NAME; the chips are what tell two people apart.
 */
export function staffLabel(u: StaffLike, opts: StaffLabelOpts = {}): ReactNode {
  const name = String(u.fullName ?? '').trim() || '—';
  const mobile = String(u.mobile ?? '').trim();
  const terr = opts.territories?.get(String(u.territoryId ?? ''));
  return (
    // `flex-wrap`: a popup only gets `min(32rem, available width)`, so on a phone the avatar, the name's
    // floor and three chips do not fit on one line — and the kit's popup clips rather than scrolls
    // sideways, which silently ate the phone number. Wrapping is the only behaviour that keeps every
    // part readable; on a normal screen nothing wraps and the row is unchanged.
    <span className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
      <EntityAvatar name={name} src={u.profilePhotoUrl} size={opts.size ?? 26} />
      {/* A FLOOR on the name. The kit caps a Select popup at 32rem, and an avatar plus three chips can
          eat all of it — which truncated a name as short as "Vikram RSM" to "Vikram …". The chips give
          up their slack first: the name is the thing being chosen. */}
      <span className="min-w-26 flex-1 truncate" title={name}>{name}</span>
      {opts.badges ?? null}
      {/* The identifiers travel as ONE group, pinned to the right edge. When the row has to wrap they
          drop together and stay right-aligned, reading as the continuation of the line above; loose
          children wrap one at a time and the straggler lands under the avatar looking like a new row. */}
      <span className="ms-auto flex flex-wrap items-center justify-end gap-2">
        {terr?.code ? <TerritoryCode code={String(terr.code)} name={terr.name} /> : null}
        {mobile ? <StaffPhone mobile={mobile} /> : null}
        {opts.note ?? null}
      </span>
    </span>
  );
}

/** What the collapsed trigger shows — mark + name only. The chips make the LIST scannable; in a
 *  one-row trigger they are just width, and width is what clips the name. */
export function staffSelectedText(u: StaffLike, size = 22): ReactNode {
  const name = String(u.fullName ?? '').trim() || '—';
  return (
    <span className="flex min-w-0 max-w-full items-center gap-2">
      <EntityAvatar name={name} src={u.profilePhotoUrl} size={size} />
      <span className="min-w-0 truncate">{name}</span>
    </span>
  );
}

/**
 * Everything one staff option needs. Spread it: `{ value: String(u.id), ...staffOption(u, { … }) }`,
 * and point the Select at `optionFilterProp="searchLabel"` + `optionLabelProp="selectedText"`, with
 * `popupMatchSelectWidth={false}` so the open list sizes to the chips instead of to the trigger.
 */
export function staffOption(u: StaffLike, opts: StaffLabelOpts = {}) {
  return {
    label: staffLabel(u, opts),
    selectedText: staffSelectedText(u),
    searchLabel: staffText(u),
    title: staffText(u),
  };
}
